import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { seatLabel } from '@mahjong/core';
import {
  AppBar,
  BarAction,
  Body,
  Button,
  Card,
  ErrorBox,
  Label,
  RankBadge,
  Screen,
  Spinner,
  cx,
} from '../components/ui';
import { useGroupToken } from '../hooks/useGroup';
import { api, ApiError } from '../lib/api';
import { formatLongDateTime, formatPoint, formatScore } from '../lib/format';

/**
 * 対局の詳細（画面案6 / §9.2）。
 *
 * 内訳を横に並べて合計の組み立てを追えるようにする。飛び賞は金額より
 * 「誰から誰へ」が重要なので、表とは別に授受の行を独立させる。
 */
export function MatchDetailPage() {
  const { groupId, matchId } = useParams<{ groupId: string; matchId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const token = useGroupToken(groupId);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const match = useQuery({
    queryKey: ['match', groupId, matchId],
    queryFn: ({ signal }) => api.getMatch(groupId!, token!, matchId!, signal),
    enabled: Boolean(groupId && matchId && token),
  });

  const remove = useMutation({
    mutationFn: () => api.deleteMatch(groupId!, token!, matchId!),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['matches', groupId] });
      void queryClient.invalidateQueries({ queryKey: ['group', groupId] });
      navigate(`/g/${groupId}`);
    },
  });

  if (!token) {
    return (
      <Screen>
        <AppBar title="対局" backTo="/" />
        <Body>
          <ErrorBox message="この端末に参加情報がありません。共有URLから開いてください。" />
        </Body>
      </Screen>
    );
  }

  if (match.isLoading) {
    return (
      <Screen>
        <AppBar title="対局" backTo={`/g/${groupId}`} />
        <Body>
          <Spinner />
        </Body>
      </Screen>
    );
  }

  if (match.isError || !match.data) {
    return (
      <Screen>
        <AppBar title="対局" backTo={`/g/${groupId}`} />
        <Body>
          <ErrorBox
            title="表示できませんでした"
            message={match.error instanceof ApiError ? match.error.message : '通信に失敗しました'}
          />
        </Body>
      </Screen>
    );
  }

  const m = match.data;
  const rule = m.ruleSnapshot;
  const nameOf = (id: string) =>
    m.results.find((r) => r.memberId === id)?.nameSnapshot ?? '不明';

  // 飛び賞の授受。results から復元する（誰が誰に払ったか）
  const transfers = m.results
    .filter((r) => r.isBankrupt && r.bankruptcyPoint < 0)
    .map((r) => {
      // payTo=buster なら bustedBy、特定できなければトップが受け取っている
      const receiverId = r.bustedBy ?? m.results.find((x) => x.rank === 1)?.memberId ?? null;
      return { from: r.memberId, to: receiverId, point: Math.abs(r.bankruptcyPoint) };
    });

  return (
    <Screen>
      <AppBar
        title={formatLongDateTime(m.playedAt)}
        subtitle={m.memo}
        backTo={`/g/${groupId}`}
        action={
          <div className="flex items-center gap-3">
            <Link to={`/g/${groupId}/matches/${matchId}/edit`} className="text-felt-accent">
              編集
            </Link>
            <BarAction onClick={() => setConfirmDelete(true)} className="text-[#F09A92]">
              削除
            </BarAction>
          </div>
        }
      />

      <Body>
        {/* --- 順位表と内訳 --- */}
        <Card className="overflow-x-auto">
          <table className="w-full border-collapse text-[12.5px] tabular-nums">
            <thead>
              <tr>
                <th className="border-b border-line pb-1.5 text-left text-[10.5px] font-medium text-ink-faint">
                  &nbsp;
                </th>
                {['素点', '素点pt', 'ウマ', 'オカ', '飛び', '合計'].map((h) => (
                  <th
                    key={h}
                    className="border-b border-line pb-1.5 pl-2 text-right text-[10.5px] font-medium text-ink-faint"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {m.results.map((r) => (
                <tr key={r.memberId}>
                  <td className="border-b border-line py-2">
                    <span className="flex items-center gap-1.5 text-[13px] font-bold">
                      <RankBadge rank={r.rank} />
                      <span className="whitespace-nowrap">{r.nameSnapshot}</span>
                      {r.seat && (
                        <span className="text-[10px] font-normal text-ink-faint">
                          {seatLabel(r.seat)}
                        </span>
                      )}
                    </span>
                    {r.tiedButLower && (
                      <span className="mt-0.5 inline-block rounded border border-[#E4CE9A] px-1 text-[10px] text-gold">
                        同点（席順で下位）
                      </span>
                    )}
                  </td>
                  <td className="border-b border-line py-2 pl-2 text-right">
                    {formatScore(r.rawScore)}
                  </td>
                  <td className="border-b border-line py-2 pl-2 text-right">
                    {r.scorePoint === 0 ? '0' : formatPoint(r.scorePoint)}
                  </td>
                  <td className="border-b border-line py-2 pl-2 text-right">
                    {r.umaPoint === 0 ? '—' : formatPoint(r.umaPoint)}
                  </td>
                  <td className="border-b border-line py-2 pl-2 text-right">
                    {r.okaPoint === 0 ? '—' : formatPoint(r.okaPoint)}
                  </td>
                  <td className="border-b border-line py-2 pl-2 text-right">
                    {r.bankruptcyPoint === 0 ? '—' : formatPoint(r.bankruptcyPoint)}
                  </td>
                  <td
                    className={cx(
                      'border-b border-line py-2 pl-2 text-right text-[15px] font-black',
                      r.totalPoint >= 0 ? 'pt-plus' : 'pt-minus',
                    )}
                  >
                    {formatPoint(r.totalPoint)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>

        {/* --- 飛び賞の授受（画面案6） --- */}
        {transfers.map((t, i) => (
          <div
            key={i}
            className="flex items-center gap-2 rounded-lg bg-[#FBF3E2] px-3 py-2.5 text-[12.5px] text-[#7A5A14]"
          >
            <span>飛び賞</span>
            <span className="font-bold text-gold">
              {nameOf(t.from)} → {t.to ? nameOf(t.to) : '（受取なし）'}
            </span>
            <span className="ml-auto font-bold">{t.point}pt</span>
          </div>
        ))}

        {/* --- 適用したルール --- */}
        <Card>
          <Label>この対局に適用したルール</Label>
          <div className="mt-2 text-[12.5px] leading-loose text-ink-soft">
            {rule.startPoints.toLocaleString('ja-JP')}持ち /{' '}
            {rule.returnPoints.toLocaleString('ja-JP')}返し / ウマ{' '}
            {rule.uma.map((u) => formatPoint(u)).join(' ')}
            <br />
            {
              {
                gosha_rokunyu: '五捨六入',
                shisha_gonyu: '四捨五入',
                ceil: '切り上げ',
                floor: '切り捨て',
              }[rule.rounding]
            }
            {rule.bankruptcy.enabled &&
              ` / 飛び賞 ${rule.bankruptcy.point}pt（${
                rule.bankruptcy.payTo === 'buster' ? '飛ばした人が受け取る' : 'トップが受け取る'
              }）`}
          </div>
        </Card>

        <Label className="leading-relaxed">
          記録後にルールを変更しても、この対局の計算結果は変わりません。
        </Label>
      </Body>

      {/* --- 削除の確認（R-05） --- */}
      {confirmDelete && (
        <div className="fixed inset-0 z-20 flex items-center justify-center p-6">
          <button
            type="button"
            aria-label="閉じる"
            onClick={() => setConfirmDelete(false)}
            className="absolute inset-0 bg-felt/45"
          />
          <div className="relative z-10 w-full max-w-xs rounded-2xl bg-white p-5">
            <div className="text-base font-bold">この対局を削除しますか</div>
            <div className="mt-1 text-[12.5px] leading-relaxed text-ink-soft">
              削除すると累計ポイントとランキングから外れます。元に戻せません。
            </div>
            {remove.isError && (
              <div className="mt-3">
                <ErrorBox
                  message={
                    remove.error instanceof ApiError ? remove.error.message : '削除できませんでした'
                  }
                />
              </div>
            )}
            <div className="mt-4 flex flex-col gap-2">
              <Button
                onClick={() => remove.mutate()}
                disabled={remove.isPending}
                className="bg-red text-white"
              >
                {remove.isPending ? '削除しています…' : '削除する'}
              </Button>
              <Button variant="ghost" onClick={() => setConfirmDelete(false)}>
                やめる
              </Button>
            </div>
          </div>
        </div>
      )}
    </Screen>
  );
}
